import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, FileText, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AppHeader } from "@/components/AppHeader";
import { SectionLabel } from "@/components/FormField";
import { JobsTable } from "@/components/JobsTable";
import { JobEntryForm } from "@/components/JobEntryForm";
import { CsvImport } from "@/components/admin/CsvImport";
import { BusinessProfileEditor } from "@/components/admin/BusinessProfileEditor";
import { TeamPanel } from "@/components/admin/TeamPanel";
import { useBusiness } from "@/context/BusinessContext";
import { api, formatApiError } from "@/lib/api";
import { exportCSV, exportPDF } from "@/lib/exporters";
import { ui } from "@/lib/ui";

const TABS = [
  ["jobs", "Dispatch", "admin-tab-jobs"],
  ["manual", "Log job", "admin-tab-manual-entry"],
  ["profile", "Business profile", "admin-tab-business-profile"],
  ["team", "Team", "admin-tab-team"],
];

const JobsPane = ({ jobs, reload, onUpdated, company }) => {
  const empty = !jobs || jobs.length === 0;
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="font-mono text-xs uppercase tracking-wider text-slate-400" data-testid="admin-job-count">
          {jobs ? `${jobs.length} jobs · newest first` : "loading"}
        </p>
        <div className="flex flex-wrap gap-2">
          <Button disabled={empty} onClick={() => exportCSV(jobs, company)} className={`${ui.btnSecondary} h-10`} data-testid="export-jobs-csv-button">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Export to CSV
          </Button>
          <Button disabled={empty} onClick={() => exportPDF(jobs, company)} className={`${ui.btnMagenta} h-10`} data-testid="export-jobs-pdf-button">
            <FileText className="w-4 h-4 mr-2" />Export to PDF
          </Button>
        </div>
      </div>
      <CsvImport onImported={reload} />
      {jobs === null && <div className="py-16 grid place-items-center text-cyan-400"><Loader2 className="w-6 h-6 animate-spin" /></div>}
      {jobs?.length === 0 && (
        <div className="border border-dashed border-slate-700 py-16 text-center" data-testid="admin-jobs-empty">
          <p className="font-display text-sm uppercase tracking-widest">No jobs yet</p>
          <p className="text-sm text-slate-500 mt-2">Customer requests, staff entries and imports will appear here.</p>
        </div>
      )}
      {jobs?.length > 0 && <JobsTable jobs={jobs} onUpdated={onUpdated} />}
    </div>
  );
};

export default function AdminDashboard() {
  const { profile } = useBusiness();
  const [jobs, setJobs] = useState(null);
  const [tab, setTab] = useState("jobs");

  const load = useCallback(() => {
    api.get("/jobs").then((r) => setJobs(r.data)).catch((e) => {
      toast.error(formatApiError(e));
      setJobs([]);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changeTab = (t) => {
    setTab(t);
    if (t === "jobs") {
      load();
    }
  };

  return (
    <div className="min-h-screen bg-[#030712] cyber-grid">
      <AppHeader />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        <div className="rise-in mb-8">
          <SectionLabel>dispatcher console</SectionLabel>
          <h1 className="font-display text-3xl sm:text-4xl uppercase tracking-wider mt-3">Operations</h1>
        </div>
        <Tabs value={tab} onValueChange={changeTab} className="rise-in" style={{ animationDelay: "80ms" }}>
          <TabsList className="rounded-none bg-[#070B16] border border-slate-800 h-auto p-1 flex flex-wrap justify-start gap-1 mb-8">
            {TABS.map(([v, label, id]) => (
              <TabsTrigger key={v} value={v} data-testid={id}
                className="rounded-none font-mono text-xs uppercase tracking-wider text-slate-400 px-4 py-2 data-[state=active]:bg-cyan-400 data-[state=active]:text-black data-[state=active]:shadow-[0_0_16px_rgba(0,245,255,0.5)]">
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
          <TabsContent value="jobs">
            <JobsPane jobs={jobs} reload={load} company={profile?.company_name || "Towing"}
              onUpdated={(j) => setJobs((list) => list.map((x) => (x.id === j.id ? j : x)))} />
          </TabsContent>
          <TabsContent value="manual"><div className="max-w-2xl"><JobEntryForm /></div></TabsContent>
          <TabsContent value="profile">{profile && <BusinessProfileEditor />}</TabsContent>
          <TabsContent value="team"><TeamPanel /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
